import { Typography } from '@euler/app/components/typography';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { Panel } from '@euler/app/tv/components/Panel';
import { StarIcon } from '@euler/app/tv/components/icons/StartIcon';
import { SearchBookmarksManager } from '@euler/app/tv/functions/SearchBookmarksManager';
import { useSearchCriteriaContext } from '@euler/app/tv/functions/SearchCriteriaContext';
import { SearchCriteria } from '@euler/app/tv/functions/search-criteria/types';
import {
  buildSearchCriteriaBookmark,
  buildSearchCriteriaKey,
} from '@euler/app/tv/functions/search-criteria/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { withConfirmation } from '@euler/utils';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { memo, useCallback, useMemo } from 'react';
import {
  FlatList,
  ListRenderItem,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const SearchCriteriaBookmarksPanel = memo(
  ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const [savedSearches] = useBehaviorSubject(
      SearchBookmarksManager.shared.searchBookmarks$,
    );

    const { apply } = useSearchCriteriaContext();

    const onPress = useCallback(
      (criteria: SearchCriteria) => {
        apply(criteria);
      },
      [apply],
    );

    const onRemove = useCallback(async (key: string) => {
      if (
        await withConfirmation({
          title: '确认',
          message: '是否确认删除该常用搜索?',
        })
      ) {
        await SearchBookmarksManager.shared.removeSearchCriteriaBookmarkByKey(
          key,
        );
      }
    }, []);

    const renderItem: ListRenderItem<SearchCriteria> = useCallback(
      ({ item, index }) => {
        return (
          <Cell
            criteria={item}
            first={index === 0}
            onPress={onPress}
            onRemove={onRemove}
          />
        );
      },
      [onPress, onRemove],
    );

    return (
      <Panel
        title="常用搜索"
        contentContainerStyle={{
          flex: 1,
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
        }}
        headerStyle={{
          marginTop: rpx(20),
          marginBottom: rpx(10),
        }}
        titleStyle={{ fontSize: rpx(28), lineHeight: rpx(44) }}
        style={style}
      >
        {!savedSearches?.length ? (
          <EmptyPlaceholder
            icon={
              <Icon
                name="file-tray-outline"
                size={rpx(60)}
                color={theme.emptyPlaceholder.iconColor}
              />
            }
            text="暂无相关常用搜索，您可以将搜索条件进行保存以便快速访问"
            textStyle={{
              fontSize: rpx(22),
              lineHeight: rpx(36),
              marginTop: rpx(20),
              marginLeft: rpx(20),
              marginRight: rpx(20),
            }}
            css={`
              flex: 1;
              top: ${-rpx(20)}px;
            `}
          />
        ) : (
          <FlatList
            data={savedSearches ?? []}
            keyExtractor={x => buildSearchCriteriaKey(x)}
            renderItem={renderItem}
          />
        )}
      </Panel>
    );
  },
);

const Cell = memo(
  ({
    criteria,
    first,
    style,
    onPress,
    onRemove,
  }: {
    criteria: SearchCriteria;
    style?: StyleProp<ViewStyle>;
    first?: boolean;
    onPress?: (criteria: SearchCriteria) => void;
    onRemove?: (key: string) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const bookmark = useMemo(
      () => buildSearchCriteriaBookmark(criteria),
      [criteria],
    );

    const handlePress = useCallback(() => {
      onPress?.(criteria);
    }, [onPress, criteria]);

    const onRemovePress = useCallback(() => {
      onRemove?.(bookmark.key);
    }, [bookmark.key, onRemove]);

    const {
      iconColor,
      titleTextColor,
      tagTextColor,
      tagBorderColor,
      separatorLineColor,
    } = theme.search.bookmark;

    return (
      <TouchableOpacity
        css={`
          padding: ${rpx(16)}px ${rpx(32)}px;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
        style={style}
        onPress={handlePress}
      >
        {!first && (
          <View
            css={`
              height: 1px;
              position: absolute;
              left: ${rpx(32)}px;
              right: 0;
              top: 0;
              background-color: ${separatorLineColor};
            `}
          />
        )}
        <TouchableOpacity onPress={onRemovePress}>
          <StarIcon size={rpx(28)} color={iconColor} />
        </TouchableOpacity>
        <View
          css={`
            flex: 1;
            margin-left: ${rpx(16)}px;
          `}
        >
          <Typography.Label
            regular
            size={rpx(22)}
            lineHeight={rpx(30)}
            color={titleTextColor}
          >
            {bookmark.title}
          </Typography.Label>
          {bookmark.tags.length > 0 && (
            <HStack
              justifyContent="flex-start"
              css={`
                flex-wrap: wrap;
                margin-top: ${rpx(8)}px;
              `}
            >
              {bookmark.tags.map(tag => (
                <View
                  key={tag}
                  css={`
                    border-width: 1px;
                    border-color: ${tagBorderColor};
                    padding: 1px ${rpx(6)}px;
                    margin-right: ${rpx(8)}px;
                    margin-bottom: ${rpx(5)}px;
                    border-radius: ${rpx(5)}px;
                  `}
                >
                  <Typography.Label
                    light
                    size={rpx(16)}
                    lineHeight={rpx(20)}
                    color={tagTextColor}
                  >
                    {tag}
                  </Typography.Label>
                </View>
              ))}
            </HStack>
          )}
        </View>
      </TouchableOpacity>
    );
  },
);
